import React, { useEffect, useMemo, useState } from 'react';
import { Task, TaskStatusDef, TaskStatusCategory } from '../../../types/task';
import { usePermissions } from '../../../hooks/usePermissions';
import toast from 'react-hot-toast';
import { TaskCard } from './taskMeta';
import { useTaskStatuses } from '../../../hooks/useTaskStatuses';
import { PlusIcon, EyeIcon, EyeSlashIcon, EllipsisHorizontalIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const PALETTE = ['#9ca3af', '#0ea5e9', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#f59e0b', '#10b981', '#14b8a6', '#f43f5e', '#0f7f75', '#64748b'];
const CATEGORY_LABEL: Record<TaskStatusCategory, string> = { todo: 'Teendő (nyitott)', in_progress: 'Folyamatban (aktív)', completed: 'Kész (lezárt)', cancelled: 'Törölve' };
const newId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10));

interface Props {
  tasks: Task[];
  onUpdateStatus: (taskId: string, status: Task['status'], statusId?: string) => void;
  onView: (task: Task) => void;
  onNew?: (status: Task['status']) => void;
  projectNames?: Record<string, string>;
}

const TaskBoard: React.FC<Props> = ({ tasks, onUpdateStatus, onView, onNew, projectNames = {} }) => {
  const { statuses, statusOf, save } = useTaskStatuses();
  const { isAdmin } = usePermissions();
  const [over, setOver] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!menuFor) return;
    const h = () => setMenuFor(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [menuFor]);

  const persist = async (next: TaskStatusDef[], ok?: string) => {
    try { setSaving(true); await save(next.map((x, i) => ({ ...x, order: i }))); if (ok) toast.success(ok); }
    catch (e: any) { toast.error(e?.message || 'Mentés sikertelen'); }
    finally { setSaving(false); }
  };
  const addColumn = async () => {
    const name = newName.trim();
    if (!name) return;
    await persist([...statuses, { id: newId(), name, color: PALETTE[statuses.length % PALETTE.length], category: 'in_progress', order: statuses.length }], `„${name}” oszlop hozzáadva`);
    setNewName(''); setAdding(false);
  };
  const updateColumn = (id: string, patch: Partial<TaskStatusDef>) => persist(statuses.map(x => (x.id === id ? { ...x, ...patch } : x)));
  const moveColumn = (id: string, dir: -1 | 1) => {
    const i = statuses.findIndex(x => x.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= statuses.length) return;
    const next = [...statuses]; [next[i], next[j]] = [next[j], next[i]];
    return persist(next);
  };
  const deleteColumn = (col: TaskStatusDef) => {
    if ((byStatus[col.id] || []).length) { toast.error('Előbb húzd át a feladatokat egy másik oszlopba'); return; }
    const rest = statuses.filter(x => x.id !== col.id);
    if (!rest.some(x => x.category === 'todo') || !rest.some(x => x.category === 'completed')) { toast.error('Legalább egy Teendő és egy Kész oszlop kell'); return; }
    if (!window.confirm(`Törlöd a(z) „${col.name}” oszlopot?`)) return;
    return persist(rest, 'Oszlop törölve');
  };
  const [dragging, setDragging] = useState<string | null>(null);
  const [hideDone, setHideDone] = useState<boolean>(() => { try { return localStorage.getItem('tasks.board.hideDone') !== '0'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem('tasks.board.hideDone', hideDone ? '1' : '0'); } catch { /* ignore */ } }, [hideDone]);
  const isClosed = (cat: string) => cat === 'completed' || cat === 'cancelled';

  const byStatus = useMemo(() => {
    const m: Record<string, Task[]> = {};
    statuses.forEach(s => { m[s.id] = []; });
    tasks.forEach(t => { const s = statusOf(t); (m[s.id] || (m[s.id] = [])).push(t); });
    const rank = { urgent: 0, high: 1, medium: 2, low: 3 } as Record<string, number>;
    Object.values(m).forEach(list => list.sort((a, b) => (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9)));
    return m;
  }, [tasks, statuses, statusOf]);

  const onDrop = (e: React.DragEvent, statusId: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/task-id') || dragging;
    setOver(null); setDragging(null);
    if (!id) return;
    const t = tasks.find(x => x.id === id);
    const col = statuses.find(s => s.id === statusId);
    if (t && col && statusOf(t).id !== statusId) onUpdateStatus(id, col.category, col.id);
  };

  const hiddenCount = statuses.filter(s => isClosed(s.category)).reduce((n, s) => n + (byStatus[s.id]?.length || 0), 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <button type="button" onClick={() => setHideDone(h => !h)} className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md border text-xs ${hideDone ? 'border-primary-300 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800' : 'border-[var(--crm-border)] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'}`} title="Kész és Törölve oszlopok elrejtése (húzással továbbra is ide tehető a feladat)">
          {hideDone ? <EyeSlashIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}Kész / Törölve oszlopok{hideDone && hiddenCount ? ` (${hiddenCount} rejtve)` : ''}
        </button>
      </div>
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
      {statuses.map(col => {
        const list = byStatus[col.id] || [];
        if (hideDone && isClosed(col.category)) {
          return (
            <div
              key={col.id}
              onDragOver={(e) => { e.preventDefault(); if (over !== col.id) setOver(col.id); }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => onDrop(e, col.id)}
              onClick={() => setHideDone(false)}
              title={`${col.name} – ${list.length} feladat (kattints a megnyitáshoz, vagy húzd ide a feladatot)`}
              className={`flex-shrink-0 w-11 snap-start rounded-xl border flex flex-col items-center py-3 gap-2 cursor-pointer transition-colors ${over === col.id ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-gray-800/60' : 'border-dashed border-[var(--crm-border)] bg-[var(--crm-bg)] hover:border-gray-400'}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
              <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">{col.name}</span>
              <span className="text-[11px] text-gray-400 tabular-nums">{list.length}</span>
            </div>
          );
        }
        return (
          <div
            key={col.id}
            onDragOver={(e) => { e.preventDefault(); if (over !== col.id) setOver(col.id); }}
            onDragLeave={() => setOver(null)}
            onDrop={(e) => onDrop(e, col.id)}
            className={`flex-shrink-0 w-[280px] sm:w-[300px] snap-start rounded-xl border transition-colors flex flex-col max-h-[calc(100vh-280px)] ${over === col.id ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-gray-800/60' : 'border-[var(--crm-border)] bg-[var(--crm-bg)]'}`}
          >
            <div className="px-3 py-2.5 flex items-center justify-between gap-1 relative">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: col.color }} />
                {renaming === col.id ? (
                  <input
                    autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                    onBlur={() => { const v = renameVal.trim(); setRenaming(null); if (v && v !== col.name) updateColumn(col.id, { name: v }); }}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenaming(null); }}
                    className="crm-input-raw h-7 px-1.5 text-sm rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] w-40 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                ) : (
                  <span className="truncate" onDoubleClick={() => { if (isAdmin) { setRenaming(col.id); setRenameVal(col.name); } }} title={isAdmin ? 'Dupla kattintás: átnevezés' : col.name}>{col.name}</span>
                )}
                <span className="text-xs text-gray-400 tabular-nums">{list.length}</span>
              </div>
              <div className="flex items-center flex-shrink-0">
                {onNew && col.category !== 'cancelled' && (
                  <button type="button" onClick={() => onNew(col.category)} className="p-1 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-white" aria-label="Új feladat"><PlusIcon className="w-4 h-4" /></button>
                )}
                {isAdmin && (
                  <button type="button" onClick={e => { e.stopPropagation(); setMenuFor(menuFor === col.id ? null : col.id); }} className="p-1 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-white" aria-label="Oszlop beállításai"><EllipsisHorizontalIcon className="w-4 h-4" /></button>
                )}
              </div>
              {menuFor === col.id && (
                <div onClick={e => e.stopPropagation()} className="absolute right-2 top-10 z-40 w-60 rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] shadow-xl p-2 space-y-2 text-sm">
                  <button type="button" onClick={() => { setRenaming(col.id); setRenameVal(col.name); setMenuFor(null); }} className="w-full flex items-center gap-2 h-8 px-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-left"><PencilIcon className="w-4 h-4 text-gray-400" />Átnevezés</button>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 px-2 mb-1">Szín</div>
                    <div className="flex flex-wrap gap-1 px-2">
                      {PALETTE.map(c => <button key={c} type="button" onClick={() => updateColumn(col.id, { color: c })} className={`w-5 h-5 rounded-full border-2 ${col.color === c ? 'border-gray-900 dark:border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} aria-label={c} />)}
                    </div>
                  </div>
                  <label className="block px-2">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Típus</div>
                    <select value={col.category} onChange={e => updateColumn(col.id, { category: e.target.value as TaskStatusCategory })} className="w-full h-8 pl-2 pr-7 text-xs border rounded-md">
                      {(Object.keys(CATEGORY_LABEL) as TaskStatusCategory[]).map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                    </select>
                  </label>
                  <div className="flex gap-1 px-2">
                    <button type="button" onClick={() => moveColumn(col.id, -1)} disabled={statuses[0]?.id === col.id} className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md border border-[var(--crm-border)] text-xs disabled:opacity-40"><ChevronLeftIcon className="w-3.5 h-3.5" />Balra</button>
                    <button type="button" onClick={() => moveColumn(col.id, 1)} disabled={statuses[statuses.length - 1]?.id === col.id} className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md border border-[var(--crm-border)] text-xs disabled:opacity-40">Jobbra<ChevronRightIcon className="w-3.5 h-3.5" /></button>
                  </div>
                  <button type="button" onClick={() => { setMenuFor(null); deleteColumn(col); }} className="w-full flex items-center gap-2 h-8 px-2 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-left"><TrashIcon className="w-4 h-4" />Oszlop törlése</button>
                </div>
              )}
            </div>
            <div className="px-2 pb-2 space-y-2 overflow-y-auto flex-1 min-h-[80px]">
              {list.length === 0 && <div className="text-xs text-gray-400 text-center py-6">Húzd ide a feladatot</div>}
              {list.map(t => (
                <TaskCard
                  key={t.id}
                  task={t}
                  draggable
                  onDragStart={(e, task) => { e.dataTransfer.setData('text/task-id', task.id); e.dataTransfer.effectAllowed = 'move'; setDragging(task.id); }}
                  onClick={onView}
                  projectName={t.projectId ? projectNames[t.projectId] : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}
      {isAdmin && (
        <div className="flex-shrink-0 w-[260px] snap-start">
          {adding ? (
            <div className="rounded-xl border border-[var(--crm-border)] bg-[var(--crm-surface)] p-2 space-y-2">
              <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addColumn(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }} placeholder="Oszlop neve…" className="crm-input-raw w-full h-9 px-2.5 text-sm rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] focus:outline-none focus:ring-1 focus:ring-primary-500" disabled={saving} />
              <div className="flex gap-1.5">
                <button type="button" onClick={addColumn} disabled={!newName.trim() || saving} className="crm-btn crm-btn-primary h-8 text-xs">Hozzáadás</button>
                <button type="button" onClick={() => { setAdding(false); setNewName(''); }} className="crm-btn crm-btn-ghost h-8 text-xs">Mégse</button>
              </div>
              <p className="text-[11px] text-gray-400">Az új oszlop „Folyamatban” típusú lesz; a ⋯ menüben átállítható.</p>
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className="w-full h-11 rounded-xl border border-dashed border-[var(--crm-border)] text-sm text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-800 dark:hover:text-gray-100 inline-flex items-center justify-center gap-1.5"><PlusIcon className="w-4 h-4" />Oszlop hozzáadása</button>
          )}
        </div>
      )}
    </div>
    </div>
  );
};

export default TaskBoard;
